import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import cn from "clsx";
import moment from "moment";
import { otp_resend_timeout } from "@/config/app";
import { ButtonWithLoader } from "../../common/buttons";
import CommonAnalyticsEvent from "@/services/analytics/main/common";
import {
  TurnstileCaptchaRef,
  TurnstileCaptcha
} from "@/components/captcha/captcha";
import { useAppSelector } from "@/redux/store";
import {
  selectSettings,
  selectTurnstileCaptchaEnabledKeys
} from "@/util/selectors";
import "./style.scss";

const LRM = "\u200E";

type SendOtpFn =
  | ((
      e?: React.MouseEvent<HTMLSpanElement>,
      isNewSignUp?: boolean,
      resend?: boolean
    ) => void | Promise<void>)
  | (() => void | Promise<void>);

const SendOTP = ({
  phone,
  otp,
  handleOTPChange,
  verifyOtp,
  otpValid,
  sendOtp,
  sendOtpCount,
  errorMessage,
  showLoader,
  otpResendTimeout,
  isNewSignUp = false,
  handleEditClick,
  disableResendCaptcha = false
}: {
  phone: string;
  otp: string;
  handleOTPChange: any;
  verifyOtp: () => Promise<void>;
  otpValid: boolean;
  sendOtp?: SendOtpFn;
  sendOtpCount: number;
  errorMessage: any;
  showLoader: boolean;
  otpResendTimeout?: number;
  isNewSignUp?: boolean;
  handleEditClick?: () => void;
  disableResendCaptcha?: boolean;
}) => {
  const { t } = useTranslation(["orderSuccess", "auth", "signUp"]);
  const { enableSendOrResendSmsOtpCaptcha } = useAppSelector(
    selectTurnstileCaptchaEnabledKeys
  );

  const [timeoutActive, setTimeoutActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState<number>(
    +(otpResendTimeout ?? otp_resend_timeout)
  );
  const [countdownId, setCountdownId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [isInteractiveCaptcha, setIsInteractiveCaptcha] = useState(false);
  const [showCaptchaForResend, setShowCaptchaForResend] = useState(false);
  const [captchaCycleKey, setCaptchaCycleKey] = useState(0);
  const [isResendCaptchaVerified, setIsResendCaptchaVerified] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const turnstileCaptchaToken = useAppSelector(
    state => state.authReducer.turnstileCaptchaToken
  );
  const settings = useAppSelector(selectSettings);
  const valueCaptchaFromCMS = settings?.captcha?.sendOrResendSmsOtp;
  const isAutomaticCaptcha =
    !captchaLoading &&
    !isInteractiveCaptcha &&
    valueCaptchaFromCMS &&
    !turnstileCaptchaToken;
  const shouldShowLoader = isAutomaticCaptcha || captchaLoading;

  const disabledResend = timeLeft > 0 || isResendLoading;

  const otpCaptchaRef = useRef<TurnstileCaptchaRef>(null);

  const setCountdownFlow = () => {
    if (!timeoutActive) return;
    if (!timeLeft) {
      setTimeoutActive(false);
      if (countdownId) clearTimeout(countdownId);
      return;
    }
    const id = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    setCountdownId(id);
    return () => clearTimeout(id);
  };

  const resetCountdownFlow = () => {
    setTimeoutActive(false);
    if (countdownId) clearTimeout(countdownId);
  };

  useEffect(() => {
    CommonAnalyticsEvent.requestOTP();
  }, []);

  useEffect(setCountdownFlow, [timeoutActive, timeLeft]);

  useEffect(() => {
    if (!isNewSignUp && errorMessage) {
      resetCountdownFlow();
    }
  }, [errorMessage]);

  useEffect(() => {
    if (!timeoutActive) {
      if (enableSendOrResendSmsOtpCaptcha && !disableResendCaptcha) {
        setShowCaptchaForResend(true);
        setCaptchaCycleKey(k => k + 1);
        setIsResendCaptchaVerified(false);
      } else {
        setShowCaptchaForResend(false);
        setIsResendCaptchaVerified(true);
      }
    }
  }, [timeoutActive, enableSendOrResendSmsOtpCaptcha, disableResendCaptcha]);

  const getOTPString = () =>
    moment().startOf("day").seconds(timeLeft).format("mm:ss");

  const handleResentOpt = () => {
    CommonAnalyticsEvent.resendOTP();
  };

  const resendDisabledByCaptcha =
    !disableResendCaptcha &&
    enableSendOrResendSmsOtpCaptcha &&
    showCaptchaForResend &&
    !isResendCaptchaVerified;

  const showCaptcha =
    !disableResendCaptcha &&
    enableSendOrResendSmsOtpCaptcha &&
    showCaptchaForResend;

  const showResentOTPText = Boolean(
    otpValid && sendOtpCount && !errorMessage && timeoutActive
  );
  const showResendLoader = isResendLoading;

  const handleResend = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (timeoutActive || resendDisabledByCaptcha || isResendLoading || !sendOtp)
      return;
    setIsResendLoading(true);
    try {
      if (isNewSignUp) {
        await (sendOtp as (e?: any, resend?: boolean) => void | Promise<void>)(
          e,
          true
        );
      } else {
        await (sendOtp as () => void | Promise<void>)();
      }
      setTimeLeft(+(otpResendTimeout ?? otp_resend_timeout));
      setTimeoutActive(true);
      setShowCaptchaForResend(false);
      setIsResendCaptchaVerified(false);
      handleResentOpt();
    } finally {
      setIsResendLoading(false);
    }
  };

  return (
    <div className="otp_verify generic_modal_content d-flex flex-column">
      <h2>
        {isNewSignUp ? t("auth:verifyOTP") : t("orderSuccess:OTPVerification")}
      </h2>

      <p className="grey_head">
        {isNewSignUp ? t("auth:sentOtpTo") : t("orderSuccess:sent4DigitOTP")}{" "}
        {isNewSignUp && <br />}
        <span className="phoneNumber">
          {LRM}
          {`${phone}`.includes("+") ? phone : `+${phone}`}
          {LRM}
        </span>{" "}
        {handleEditClick && (
          <span className="editPhone" onClick={handleEditClick}>
            {t("signUp:edit")}
          </span>
        )}
      </p>

      <div
        className={cn("form-group mobile_otp", {
          "flex-wrap": errorMessage && !otpValid
        })}
      >
        <OtpInput
          onChange={handleOTPChange}
          shouldAutoFocus
          numInputs={4}
          value={otp}
          separator={<span />}
        />
        {!isNewSignUp &&
          (errorMessage ? (
            <span
              className={cn("error", {
                "position-relative w-100 text-center ": !otpValid
              })}
            >
              {errorMessage}
            </span>
          ) : (
            !otpValid && (
              <span
                className={cn("error", {
                  "position-relative w-100 text-center ": errorMessage
                })}
              >
                {t("orderSuccess:invalidOtp")}
              </span>
            )
          ))}
      </div>

      {showCaptcha && (
        <div className="otp-captcha-above d-flex justify-content-center">
          <TurnstileCaptcha
            ref={otpCaptchaRef}
            key={captchaCycleKey}
            captchaId={`turnstile_captcha_otp_resend_${captchaCycleKey}`}
            className="turnstile_container d-flex"
            beforeLoading={() => setCaptchaLoading(true)}
            afterLoading={() => {
              setCaptchaLoading(false);
            }}
            onInterectiveChallangeStarted={() => {
              setIsInteractiveCaptcha(true);
            }}
            onToken={() => {
              setCaptchaLoading(false);
              setIsResendCaptchaVerified(true);
            }}
            onTokenExpired={() => {
              setIsResendCaptchaVerified(false);
              setCaptchaLoading(false);
            }}
          />
        </div>
      )}

      {sendOtp && (
        <ButtonWithLoader
          className="resend_otp_btn mb-2"
          disabled={timeoutActive || shouldShowLoader}
          showLoader={!disabledResend && shouldShowLoader}
          showLoaderWithoutClick={shouldShowLoader}
          onClick={handleResend}
        >
          {!showResendLoader && timeoutActive ? (
            <>
              {t("orderSuccess:pleaseTryResend")}
              <span className="resend_otp_time">{getOTPString()}</span>
            </>
          ) : !resendDisabledByCaptcha ? (
            t("orderSuccess:resendOTP")
          ) : null}
        </ButtonWithLoader>
      )}

      {!isNewSignUp && (
        <ButtonWithLoader
          className={cn(
            "round_btn form_black_btn small_btn position-relative",
            {
              disable_order_btn: !(otp && otp.length === 4),
              signUpVerifyButton: isNewSignUp
            }
          )}
          onClick={verifyOtp}
          disabled={showLoader || !(otp && otp.length === 4)}
          showLoader={showLoader}
        >
          {t("orderSuccess:verify")}
        </ButtonWithLoader>
      )}

      {isNewSignUp &&
        (errorMessage ? (
          <span
            className={cn("error", {
              "position-relative w-100 text-center ": !otpValid
            })}
          >
            {errorMessage}
          </span>
        ) : (
          !otpValid && (
            <span
              className={cn("error", {
                "position-relative w-100 text-center ": errorMessage
              })}
            >
              {t("orderSuccess:invalidOtp")}
            </span>
          )
        ))}

      {isNewSignUp && (
        <ButtonWithLoader
          className={cn(
            "round_btn form_black_btn small_btn position-relative",
            {
              disable_order_btn: !(otp && otp.length === 4),
              signUpVerifyButton: isNewSignUp
            }
          )}
          onClick={verifyOtp}
          disabled={showLoader || !(otp && otp.length === 4)}
          showLoader={showLoader}
        >
          {t("orderSuccess:verify")}
        </ButtonWithLoader>
      )}
    </div>
  );
};

export default SendOTP;
